/**
 * The shopper's shipping address: the hooks the account page's card and dialog use, and the pure
 * helpers between the form's text fields and the wire address (tested on their own).
 */
import {
  countryDisplayName,
  type PublicShippingDestination,
  SHIPPING_ADDRESS_FIELDS,
  type ShippingAddress,
  type ShippingAddressResponse,
  type ShippingAddressValidation,
  type StorefrontConfig,
  shippingAddressFieldIssue,
  shippingAddressSchema,
} from "@loot-os/api-contract";
import { LootApiError } from "@loot-os/client";
import { useMutation, useQuery } from "@tanstack/react-query";

import { lootFetch, qk, shippingAddressQueryOptions } from "./queries.js";
import { queryClient } from "./queryClient.js";
import { useShopper } from "./shopper.js";

/** Every field as the form holds it: text, blank for unset. */
export type AddressDraft = Record<keyof ShippingAddress, string>;

export function toDraft(address: ShippingAddress | null): AddressDraft {
  const draft = {} as AddressDraft;
  for (const field of SHIPPING_ADDRESS_FIELDS) draft[field.key] = address?.[field.key] ?? "";
  return draft;
}

/**
 * The draft as an address, or null while it does not parse: blank optional fields are left out
 * rather than stored as "", and every rule the server applies to the fields runs here too.
 */
export function draftToAddress(draft: AddressDraft): ShippingAddress | null {
  const candidate: Partial<AddressDraft> = {};
  for (const field of SHIPPING_ADDRESS_FIELDS) {
    const value = draft[field.key].trim();
    if (value) candidate[field.key] = value;
  }
  const parsed = shippingAddressSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

/** What is still wrong with a draft the form should say before submitting, or null. */
export function draftIssue(draft: AddressDraft): string | null {
  const address = draftToAddress(draft);
  if (!address) return null;
  const issue = shippingAddressFieldIssue(address);
  if (issue === "invalid_region") return "Pick the state or territory.";
  if (issue === "postal_code_mismatch") return "That postal code does not fit the country.";
  return null;
}

/**
 * The destinations this store ships to. `?? []` covers a config cached before the field existed:
 * the card then says shipping is unavailable until the next config fetch, never crashes.
 */
export function shippingDestinationsOf(
  config: Pick<StorefrontConfig, "shipping"> | { shipping?: StorefrontConfig["shipping"] },
): PublicShippingDestination[] {
  return config.shipping?.destinations ?? [];
}

/** US addresses pick a state from a list; everywhere else the region is typed. */
export function regionMode(countryCode: string): "us-state" | "text" {
  return countryCode.toUpperCase() === "US" ? "us-state" : "text";
}

/** The fields the validator's suggestion changes, so the confirm step can point at them. */
export function changedFields(
  entered: ShippingAddress,
  corrected: ShippingAddress,
): (keyof ShippingAddress)[] {
  return SHIPPING_ADDRESS_FIELDS.map((f) => f.key).filter(
    (key) => (entered[key] ?? "") !== (corrected[key] ?? ""),
  );
}

/** The address as lines a card prints: name, street, locality line, country, phone. */
export function addressLines(address: ShippingAddress): string[] {
  return [
    `${address.firstName} ${address.lastName}`,
    address.addressLine1,
    ...(address.addressLine2 ? [address.addressLine2] : []),
    `${address.locality}, ${address.region} ${address.postalCode}`,
    countryDisplayName(address.country),
    ...(address.phoneNumber ? [address.phoneNumber] : []),
  ];
}

/** What to tell the shopper when saving or checking an address failed. */
export function shippingErrorMessage(err: unknown): string {
  if (err instanceof LootApiError) {
    const reason = typeof err.details?.reason === "string" ? err.details.reason : null;
    switch (err.code) {
      case "invalid_shipping_address":
        if (reason === "unsupported_destination") return "We do not ship to that country.";
        if (reason === "postal_code_mismatch") return "That postal code does not fit the country.";
        if (reason === "invalid_region") return "Pick the state or territory.";
        return "That address is not one we can ship to.";
      case "shipping_unavailable":
        return "Shipping is currently unavailable.";
      case "address_unverifiable":
        return "We couldn't verify your address right now. Please try again.";
      case "address_country_mismatch": {
        const matched = err.details?.matchedCountryCode;
        const name = typeof matched === "string" ? countryDisplayName(matched) : "another country";
        return `This address appears to be in ${name}. Please select ${name} as the country.`;
      }
      case "rate_limited":
        return "Too many attempts. Wait a minute and try again.";
      case "account_locked":
        return "This account is locked. Contact support.";
      default:
        return err.message || "Could not save the address.";
    }
  }
  return err instanceof Error && err.message ? err.message : "Could not save the address.";
}

/** The saved address (null when none); undefined data while signed out. */
export function useShippingAddress() {
  const { sessionToken } = useShopper();
  return useQuery({ ...shippingAddressQueryOptions(), enabled: !!sessionToken });
}

/** Asks address validation what it thinks of an address; never saves. */
export function useValidateShippingAddress() {
  return useMutation({
    mutationFn: (address: ShippingAddress) =>
      lootFetch<ShippingAddressValidation>("/me/shipping-address/validate", {
        method: "POST",
        body: JSON.stringify(address),
      }),
  });
}

/** Saves the address; the card shows the stored copy as soon as the server answers. */
export function useSaveShippingAddress() {
  return useMutation({
    mutationFn: (address: ShippingAddress) =>
      lootFetch<ShippingAddressResponse>("/me/shipping-address", {
        method: "PUT",
        body: JSON.stringify(address),
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(qk.shippingAddress, saved);
      void queryClient.invalidateQueries({ queryKey: qk.shippingAddress });
    },
  });
}
